/**
 * AuthService - Supabase authentication wrapper
 * Handles all authentication operations with graceful fallback when Supabase is not configured
 */

import { supabase, isSupabaseConfigured } from '../lib/supabase';
import type { User, Profile, AuthStateChangeCallback, AuthError } from './types';
import type { AuthChangeEvent, Session, User as SupabaseUser } from '@supabase/supabase-js';

const STORAGE_KEY = 'path_game_user';

export class AuthService {
  private currentUser: User | null = null;
  private listeners: Set<AuthStateChangeCallback> = new Set();
  private _isLoading: boolean = true;

  constructor() {
    // Load cached user from localStorage for quick access
    this.loadCachedUser();
    
    // Set up auth state listener if Supabase is configured
    if (isSupabaseConfigured() && supabase) {
      this.initAuthListener();
    } else {
      // No Supabase, not loading
      this._isLoading = false;
    }
  }

  /**
   * Check if auth state is still loading
   */
  isLoading(): boolean {
    return this._isLoading;
  }

  /**
   * Initialize the auth state change listener
   */
  private initAuthListener(): void {
    if (!supabase) return;

    supabase.auth.onAuthStateChange(async (event: AuthChangeEvent, session: Session | null) => {
      // Only clear user on explicit sign-out, not on token refresh failures or other events
      if (event === 'SIGNED_OUT') {
        this.setCurrentUser(null);
      } else if (session?.user) {
        const user = await this.fetchUserProfile(session.user);
        this.setCurrentUser(user);
      }
      // Don't clear user on TOKEN_REFRESHED failures, INITIAL_SESSION with null, etc.
    });

    // Check for existing session on init
    this.checkExistingSession();
  }

  /**
   * Check for existing session on initialization
   */
  private async checkExistingSession(): Promise<void> {
    if (!supabase) {
      this._isLoading = false;
      return;
    }

    console.debug('[AuthService] Checking for existing session...');
    const startTime = Date.now();

    // Add timeout to prevent hanging (increased to 8s)
    const timeout = new Promise<null>((resolve) => 
      setTimeout(() => {
        console.warn('[AuthService] Session check timed out after 8s');
        resolve(null);
      }, 8000)
    );

    try {
      const sessionPromise = supabase.auth.getSession();
      const result = await Promise.race([sessionPromise, timeout]);
      
      console.debug(`[AuthService] Session check completed in ${Date.now() - startTime}ms`);
      
      if (result && 'data' in result && result.data?.session?.user) {
        console.debug('[AuthService] Found existing session, fetching profile...');
        const user = await this.fetchUserProfile(result.data.session.user);
        this.setCurrentUser(user);
      } else if (result === null) {
        // Timeout occurred - try to check localStorage directly for session
        console.debug('[AuthService] Timeout - checking localStorage for session hint...');
        const hasStoredSession = this.checkLocalStorageSession();
        if (hasStoredSession) {
          console.debug('[AuthService] Found stored session - will retry on next interaction');
        } else {
          console.debug('[AuthService] No stored session found');
        }
      } else {
        console.debug('[AuthService] No existing session found');
      }
    } catch (error) {
      console.warn('Error checking existing session:', error);
      // Don't clear auth state on error - just proceed without session
    } finally {
      this._isLoading = false;
      // Notify listeners that loading is complete
      this.notifyListeners();
    }
  }

  /**
   * Check if there's a session in localStorage (doesn't validate it)
   */
  private checkLocalStorageSession(): boolean {
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('sb-') && key.includes('-auth-token')) {
          const value = localStorage.getItem(key);
          if (value) {
            const parsed = JSON.parse(value);
            if (parsed?.access_token && parsed?.user) {
              return true;
            }
          }
        }
      }
    } catch (e) {
      console.warn('[AuthService] Error checking localStorage:', e);
    }
    return false;
  }

  /**
   * Clear potentially corrupted auth state from localStorage
   */
  private clearCorruptedAuthState(): void {
    console.debug('[AuthService] Clearing potentially corrupted auth state...');
    try {
      // Clear Supabase auth storage keys
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => {
        console.debug(`[AuthService] Removing localStorage key: ${key}`);
        localStorage.removeItem(key);
      });
    } catch (e) {
      console.warn('[AuthService] Failed to clear auth state:', e);
    }
  }

  /**
   * Fetch user profile from the profiles table
   */
  private async fetchUserProfile(supabaseUser: SupabaseUser): Promise<User> {
    if (!supabase) {
      return this.mapSupabaseUserToUser(supabaseUser, null);
    }

    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', supabaseUser.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching profile:', error);
      }

      return this.mapSupabaseUserToUser(supabaseUser, profile as Profile | null);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return this.mapSupabaseUserToUser(supabaseUser, null);
    }
  }

  /**
   * Map Supabase user and profile to our User type
   */
  private mapSupabaseUserToUser(supabaseUser: SupabaseUser, profile: Profile | null): User {
    const metadata = supabaseUser.user_metadata || {};
    
    return {
      id: supabaseUser.id,
      email: supabaseUser.email || null,
      username: profile?.username || metadata.username || `user_${supabaseUser.id.substring(0, 8)}`,
      displayName: profile?.display_name || metadata.full_name || metadata.name || null,
      avatarUrl: profile?.avatar_url || metadata.avatar_url || null,
      isGuest: profile?.is_guest ?? supabaseUser.is_anonymous ?? false,
      createdAt: profile?.created_at || supabaseUser.created_at,
    };
  }

  /**
   * Load cached user from localStorage
   */
  private loadCachedUser(): void {
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        this.currentUser = JSON.parse(cached);
      }
    } catch (error) {
      console.error('Error loading cached user:', error);
    }
  }

  /**
   * Save user to localStorage for quick access
   */
  private cacheUser(user: User | null): void {
    try {
      if (user) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error caching user:', error);
    }
  }

  /**
   * Set current user and notify listeners
   */
  private setCurrentUser(user: User | null): void {
    this.currentUser = user;
    this.cacheUser(user);
    this.notifyListeners();
  }

  /**
   * Notify all listeners of auth state change
   */
  private notifyListeners(): void {
    this.listeners.forEach(callback => {
      try {
        callback(this.currentUser);
      } catch (error) {
        console.error('Error in auth state listener:', error);
      }
    });
  }

  /**
   * Check if Supabase is available
   */
  private checkSupabaseAvailable(): AuthError | null {
    if (!isSupabaseConfigured() || !supabase) {
      return {
        message: 'Authentication is not configured. Please set up Supabase credentials.',
        code: 'SUPABASE_NOT_CONFIGURED',
      };
    }
    return null;
  }

  /**
   * Sign in with Google OAuth
   */
  async signInWithGoogle(): Promise<{ error: AuthError | null }> {
    const configError = this.checkSupabaseAvailable();
    if (configError) return { error: configError };

    try {
      const { error } = await supabase!.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}${import.meta.env.BASE_URL}`,
        },
      });

      if (error) {
        return { error: { message: error.message, code: error.name } };
      }

      return { error: null };
    } catch (error) {
      return { error: { message: 'Failed to sign in with Google' } };
    }
  }

  /**
   * Sign in with GitHub OAuth
   */
  async signInWithGitHub(): Promise<{ error: AuthError | null }> {
    const configError = this.checkSupabaseAvailable();
    if (configError) return { error: configError };

    try {
      const { error } = await supabase!.auth.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo: `${window.location.origin}${import.meta.env.BASE_URL}`,
        },
      });

      if (error) {
        return { error: { message: error.message, code: error.name } };
      }

      return { error: null };
    } catch (error) {
      return { error: { message: 'Failed to sign in with GitHub' } };
    }
  }

  /**
   * Sign in with email and password
   */
  async signInWithEmail(email: string, password: string): Promise<{ error: AuthError | null }> {
    const configError = this.checkSupabaseAvailable();
    if (configError) return { error: configError };

    try {
      const { error } = await supabase!.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { error: { message: error.message, code: error.name } };
      }

      return { error: null };
    } catch (error) {
      return { error: { message: 'Failed to sign in with email' } };
    }
  }

  /**
   * Sign up with email, password, and username
   */
  async signUpWithEmail(email: string, password: string, username: string): Promise<{ error: AuthError | null }> {
    const configError = this.checkSupabaseAvailable();
    if (configError) return { error: configError };

    try {
      const { error } = await supabase!.auth.signUp({
        email,
        password,
        options: {
          data: {
            username,
          },
        },
      });

      if (error) {
        return { error: { message: error.message, code: error.name } };
      }

      return { error: null };
    } catch (error) {
      return { error: { message: 'Failed to sign up' } };
    }
  }

  /**
   * Sign in anonymously (guest session)
   */
  async signInAnonymously(): Promise<{ error: AuthError | null }> {
    const configError = this.checkSupabaseAvailable();
    if (configError) return { error: configError };

    try {
      const { error } = await supabase!.auth.signInAnonymously();

      if (error) {
        return { error: { message: error.message, code: error.name } };
      }

      return { error: null };
    } catch (error) {
      return { error: { message: 'Failed to sign in as guest' } };
    }
  }

  /**
   * Convert guest account to full account
   */
  async convertGuestToUser(email: string, password: string, username: string): Promise<{ error: AuthError | null }> {
    const configError = this.checkSupabaseAvailable();
    if (configError) return { error: configError };

    if (!this.currentUser?.isGuest) {
      return { error: { message: 'Current user is not a guest', code: 'NOT_GUEST' } };
    }

    try {
      // Update email and password
      const { error: updateError } = await supabase!.auth.updateUser({
        email,
        password,
        data: {
          username,
        },
      });

      if (updateError) {
        return { error: { message: updateError.message, code: updateError.name } };
      }

      // Update the profile to mark as non-guest
      const { error: profileError } = await supabase!
        .from('profiles')
        .update({
          username,
          is_guest: false,
        })
        .eq('id', this.currentUser.id);

      if (profileError) {
        console.error('Error updating profile:', profileError);
      }

      // Refresh the user data
      const { data: { user } } = await supabase!.auth.getUser();
      if (user) {
        const updatedUser = await this.fetchUserProfile(user);
        this.setCurrentUser(updatedUser);
      }

      return { error: null };
    } catch (error) {
      return { error: { message: 'Failed to convert guest account' } };
    }
  }

  /**
   * Sign out the current user
   */
  async signOut(): Promise<{ error: AuthError | null }> {
    // Clear local state regardless of Supabase status
    this.setCurrentUser(null);

    if (!isSupabaseConfigured() || !supabase) {
      return { error: null };
    }

    try {
      const { error } = await supabase.auth.signOut();

      if (error) {
        return { error: { message: error.message, code: error.name } };
      }

      return { error: null };
    } catch (error) {
      return { error: { message: 'Failed to sign out' } };
    }
  }

  /**
   * Get the current user
   */
  getCurrentUser(): User | null {
    return this.currentUser;
  }

  /**
   * Subscribe to auth state changes
   * Returns an unsubscribe function
   */
  onAuthStateChange(callback: AuthStateChangeCallback): () => void {
    this.listeners.add(callback);
    
    // Immediately call with current state
    callback(this.currentUser);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Check if authentication is available
   */
  isAuthAvailable(): boolean {
    return isSupabaseConfigured();
  }
}

// Export a singleton instance
export const authService = new AuthService();
